import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/Auth";
import { Search, ShieldCheck, Eye, Pencil, Trash2 } from "lucide-react";
import { getAdmins, createAdminAccount, updateAdminAccount, deleteAdminAccount } from "../../business/AdminBusiness";
import { ensureCountryCode } from "../../lib/phone";
import toast from "react-hot-toast";
import { AppShell, headerPrimaryButtonClass } from '../../components/Layout/AppShell';
import { Modal } from '../../components/ui/Modal';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { Badge } from '../../components/ui/Badge';
import { RowActionsMenu } from '../../components/ui/RowActionsMenu';

const SUPREME_ADMIN_EMAIL = 'gestaonovaeradigital@gmail.com';

const inputClass = "w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent";
const labelClass = "block text-sm font-medium text-gray-700 mb-1";
const primaryButtonClass = "inline-flex items-center justify-center rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-60 flex-1";
const secondaryButtonClass = "rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex-1";

export const Collaborator = () => {
  const { user } = useContext(AuthContext);

  const [admins, setAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [newAdmin, setNewAdmin] = useState({
    username: '', email: '', phone: '', password: '', repeatPassword: ''
  });

  const fetchAdmins = async () => {
    try {
      setIsLoading(true);
      const response = await getAdmins();
      setAdmins(response?.data || []);
    } catch (err) {
      toast.error('Erro ao carregar administradores');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchAdmins(); }, []);

  const filteredAdmins = admins.filter((a) =>
    a?.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedAdmins = [...filteredAdmins].sort((a, b) => {
    if (sortBy === 'username') {
      const na = a.username?.toLowerCase() || '';
      const nb = b.username?.toLowerCase() || '';
      return sortOrder === 'asc' ? na.localeCompare(nb) : nb.localeCompare(na);
    }
    const da = new Date(a.createdAt || 0);
    const db = new Date(b.createdAt || 0);
    return sortOrder === 'asc' ? da - db : db - da;
  });

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
  };

  const handleCreateAdmin = async () => {
    if (!newAdmin.username || !newAdmin.email || !newAdmin.password) {
      toast.error('Nome, email e senha são obrigatórios');
      return;
    }
    if (newAdmin.password !== newAdmin.repeatPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    try {
      setIsLoading(true);
      await createAdminAccount({
        username: newAdmin.username,
        email: newAdmin.email,
        phone: ensureCountryCode(newAdmin.phone),
        password: newAdmin.password,
      });
      toast.success('Administrador criado com sucesso!');
      setShowCreateModal(false);
      setNewAdmin({ username: '', email: '', phone: '', password: '', repeatPassword: '' });
      fetchAdmins();
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Erro ao criar administrador');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditAdmin = async () => {
    if (!editingAdmin.username || !editingAdmin.email) {
      toast.error('Nome e email são obrigatórios');
      return;
    }
    try {
      setIsLoading(true);
      const payload = {
        username: editingAdmin.username,
        email: editingAdmin.email,
        phone: editingAdmin.phone,
      };
      if (editingAdmin.password) payload.password = editingAdmin.password;
      await updateAdminAccount(editingAdmin._id, payload);
      toast.success('Administrador atualizado com sucesso!');
      setShowEditModal(false);
      setEditingAdmin(null);
      fetchAdmins();
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Erro ao atualizar administrador');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAdmin = async (adminData) => {
    try {
      await deleteAdminAccount(adminData._id);
      toast.success('Administrador removido com sucesso!');
      setDeleteTarget(null);
      setSelectedPerson(null);
      fetchAdmins();
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Erro ao deletar administrador');
    }
  };

  const isSupreme = (admin) => admin?.email === SUPREME_ADMIN_EMAIL;
  const canAct = (admin) => !isSupreme(admin) || user?.email === SUPREME_ADMIN_EMAIL;

  const getInitials = (username) => {
    if (!username) return '?';
    const names = username.trim().split(' ');
    return names.length === 1
      ? names[0].charAt(0).toUpperCase()
      : (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  const getAvatarColor = (username) => {
    if (!username) return 'bg-gray-500';
    const colors = ['bg-red-500','bg-blue-500','bg-green-500','bg-yellow-500','bg-purple-500','bg-pink-500','bg-indigo-500','bg-teal-500'];
    return colors[username.charCodeAt(0) % colors.length];
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('pt-BR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const rowActionsFor = (admin) => {
    const allowed = canAct(admin);
    const blockedTitle = 'Somente o admin supremo pode gerenciar esta conta';
    return [
      { key: 'view', label: 'Ver detalhes', icon: Eye, onClick: () => setSelectedPerson(admin) },
      { key: 'edit', label: 'Editar', icon: Pencil, onClick: () => { setEditingAdmin({ ...admin, password: '' }); setShowEditModal(true); }, disabled: !allowed, disabledTitle: blockedTitle },
      { key: 'delete', label: 'Excluir', icon: Trash2, danger: true, onClick: () => setDeleteTarget(admin), disabled: !allowed || isSupreme(admin), disabledTitle: isSupreme(admin) ? 'Admin supremo não pode ser deletado' : blockedTitle },
    ];
  };

  return (
    <>
      <AppShell
        icon={ShieldCheck}
        eyebrow="Sistema"
        title="Administradores"
        meta={`${sortedAdmins.length} com acesso ao painel`}
      >
          <div className="space-y-6">

            <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm shadow-card focus:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/15"
                  placeholder="Buscar por username ou email..."
                  onChange={(e) => setSearchTerm(e.target.value)}
                  value={searchTerm}
                />
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className={headerPrimaryButtonClass}
              >
                Adicionar administrador
              </button>
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-200/70 bg-white">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-subtle">
                    <tr>
                      <th
                        className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSortChange('username')}
                      >
                        <div className="flex items-center">
                          Username
                          {sortBy === 'username' && <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                        </div>
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 hidden md:table-cell">Email</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 hidden lg:table-cell">Telefone</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Cargo</th>
                      <th
                        className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 hidden xl:table-cell cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSortChange('createdAt')}
                      >
                        <div className="flex items-center">
                          Criado em
                          {sortBy === 'createdAt' && <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                        </div>
                      </th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {isLoading ? (
                      <tr><td colSpan="6" className="py-8 px-5 text-center text-sm text-gray-400">Carregando...</td></tr>
                    ) : sortedAdmins.length > 0 ? sortedAdmins.map((admin) => (
                      <tr key={admin._id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedPerson(admin)}>
                        <td className="whitespace-nowrap px-5 py-3.5">
                          <div className="flex items-center">
                            <div className={`flex-shrink-0 h-9 w-9 rounded-full flex items-center justify-center text-white font-semibold text-sm ${getAvatarColor(admin.username)}`}>
                              {getInitials(admin.username)}
                            </div>
                            <div className="ml-3 flex items-center gap-1.5">
                              <span className="text-sm font-medium text-gray-900 truncate max-w-[140px]">{admin.username}</span>
                              {isSupreme(admin) && <ShieldCheck size={13} className="text-accent flex-shrink-0" title="Admin supremo" />}
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-5 py-3.5 hidden md:table-cell text-sm text-gray-500 truncate max-w-[180px]">{admin.email}</td>
                        <td className="whitespace-nowrap px-5 py-3.5 hidden lg:table-cell text-sm text-gray-500">{admin.phone || '—'}</td>
                        <td className="whitespace-nowrap px-5 py-3.5">
                          <Badge className="bg-red-100 text-red-700">Administrador</Badge>
                        </td>
                        <td className="whitespace-nowrap px-5 py-3.5 hidden xl:table-cell text-sm text-gray-500">{formatDate(admin.createdAt)}</td>
                        <td className="whitespace-nowrap px-5 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                          <RowActionsMenu actions={rowActionsFor(admin)} />
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="6" className="py-8 px-5 text-center text-sm text-gray-400">Nenhum administrador encontrado</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Modal criação */}
          <Modal title="Adicionar Administrador" isOpen={showCreateModal} onClose={() => setShowCreateModal(false)}>
            <div className="space-y-4">
              {[
                { label: 'Nome *', field: 'username', type: 'text', placeholder: 'João Silva' },
                { label: 'Email *', field: 'email', type: 'email', placeholder: 'joao@email.com' },
                { label: 'Telefone', field: 'phone', type: 'tel', placeholder: '+55 11 99999-9999' },
                { label: 'Senha *', field: 'password', type: 'password', placeholder: '••••••••' },
                { label: 'Repetir senha *', field: 'repeatPassword', type: 'password', placeholder: '••••••••' },
              ].map(({ label, field, type, placeholder }) => (
                <div key={field}>
                  <label className={labelClass}>{label}</label>
                  <input
                    className={inputClass}
                    type={type}
                    placeholder={placeholder}
                    value={newAdmin[field]}
                    onChange={(e) => setNewAdmin(p => ({ ...p, [field]: e.target.value }))}
                  />
                </div>
              ))}
              <div className="flex gap-2 pt-2">
                <button className={primaryButtonClass} onClick={handleCreateAdmin} disabled={isLoading}>
                  {isLoading ? 'Criando...' : 'Criar Administrador'}
                </button>
                <button
                  className={secondaryButtonClass}
                  onClick={() => { setShowCreateModal(false); setNewAdmin({ username: '', email: '', phone: '', password: '', repeatPassword: '' }); }}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </Modal>

          {/* Modal edição */}
          <Modal title="Editar Administrador" isOpen={showEditModal && !!editingAdmin} onClose={() => { setShowEditModal(false); setEditingAdmin(null); }}>
            {editingAdmin && (
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Nome *</label>
                  <input className={inputClass} type="text" value={editingAdmin.username} onChange={(e) => setEditingAdmin(p => ({ ...p, username: e.target.value }))} />
                </div>
                <div>
                  <label className={labelClass}>Email *</label>
                  <input
                    className={`${inputClass} ${isSupreme(editingAdmin) ? 'bg-gray-100 text-gray-400' : ''}`}
                    type="email"
                    value={editingAdmin.email}
                    disabled={isSupreme(editingAdmin)}
                    onChange={(e) => setEditingAdmin(p => ({ ...p, email: e.target.value }))}
                  />
                  {isSupreme(editingAdmin) && <p className="text-xs text-accent mt-1">Email do admin supremo não pode ser alterado</p>}
                </div>
                <div>
                  <label className={labelClass}>Telefone</label>
                  <input className={inputClass} type="tel" value={editingAdmin.phone || ''} onChange={(e) => setEditingAdmin(p => ({ ...p, phone: e.target.value }))} />
                </div>
                <div>
                  <label className={labelClass}>Nova Senha <span className="font-normal text-gray-400">(deixe vazio para manter)</span></label>
                  <input className={inputClass} type="password" placeholder="Nova senha" value={editingAdmin.password} onChange={(e) => setEditingAdmin(p => ({ ...p, password: e.target.value }))} />
                </div>
                <div className="flex gap-2 pt-2">
                  <button className={primaryButtonClass} onClick={handleEditAdmin} disabled={isLoading}>
                    {isLoading ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                  <button className={secondaryButtonClass} onClick={() => { setShowEditModal(false); setEditingAdmin(null); }} disabled={isLoading}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </Modal>

          {/* Modal visualização */}
          <Modal title="Detalhes do Administrador" isOpen={!!selectedPerson} onClose={() => setSelectedPerson(null)}>
            {selectedPerson && (
              <>
                <div className="flex items-center mb-6">
                  <div className={`h-14 w-14 rounded-full flex items-center justify-center text-white font-semibold text-xl ${getAvatarColor(selectedPerson.username)}`}>
                    {getInitials(selectedPerson.username)}
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold text-gray-900">{selectedPerson.username}</h3>
                      {isSupreme(selectedPerson) && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-accent-chip px-2 py-0.5 text-xs font-medium text-accent">
                          <ShieldCheck size={11} /> Supremo
                        </span>
                      )}
                    </div>
                    <Badge className="bg-red-100 text-red-700 mt-1">Administrador</Badge>
                  </div>
                </div>

                <div className="mb-2">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Informações de Contato</h4>
                  <div className="space-y-1.5">
                    {[
                      { label: 'Email:', value: selectedPerson.email },
                      { label: 'Telefone:', value: selectedPerson.phone || '—' },
                      { label: 'Criado em:', value: formatDate(selectedPerson.createdAt) },
                      { label: 'ID:', value: selectedPerson._id },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex justify-between">
                        <span className="text-sm text-gray-500">{label}</span>
                        <span className="text-sm font-medium text-gray-900 break-all">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <button
                    className={primaryButtonClass}
                    onClick={() => { setSelectedPerson(null); setEditingAdmin({ ...selectedPerson, password: '' }); setShowEditModal(true); }}
                  >
                    Editar
                  </button>
                  <button
                    className={`flex-1 rounded-md px-4 py-2 text-sm font-medium text-white ${isSupreme(selectedPerson) ? 'bg-gray-300 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
                    disabled={isSupreme(selectedPerson)}
                    onClick={() => !isSupreme(selectedPerson) && setDeleteTarget(selectedPerson)}
                    title={isSupreme(selectedPerson) ? 'Admin supremo não pode ser deletado' : 'Excluir'}
                  >
                    Excluir
                  </button>
                </div>
              </>
            )}
          </Modal>

          <ConfirmModal
            isOpen={!!deleteTarget}
            title="Excluir administrador"
            message={`Tem certeza que deseja excluir "${deleteTarget?.username}"?`}
            onCancel={() => setDeleteTarget(null)}
            onConfirm={() => handleDeleteAdmin(deleteTarget)}
          />
      </AppShell>
    </>
  );
};
